import classNames from 'classnames';
import React, { FC, useState } from 'react';
import { BsX } from 'react-icons/bs';
import { Button, ButtonSize } from '../button/button';

export type AlertActionProps = (
  | {
      type?: 'link';
      label: string;
    }
  | {
      type: 'button';
      label: string | React.ReactNode;
      buttonProps?: Omit<React.ComponentProps<typeof Button>, 'children'>;
    }
) &
  (
    | {
        href: string;
        onClick?: never;
      }
    | {
        href?: never;
        onClick: () => void;
      }
  );

export enum AlertSeverity {
  Info = 'info',
  Error = 'error',
  Success = 'success',
}

export type AlertProps = {
  action?: AlertActionProps;
  className?: string;
  icon?: React.ReactNode;
  message?: string | React.ReactNode;
  severity?: AlertSeverity | `${AlertSeverity}`;
  testId?: string;
  title?: string | React.ReactNode;
} & (
  | {
      dismissKey: string;
      onDismiss?: never;
    }
  | {
      dismissKey?: never;
      onDismiss: () => void;
    }
  | {
      dismissKey?: never;
      onDismiss?: never;
    }
);

export const AlertAction = (props: AlertActionProps) => {
  if (!props) {
    return null;
  }

  if (props.type === 'button') {
    return (
      <Button
        size={ButtonSize.Small}
        onClick={() => {
          if (props.onClick) {
            return props.onClick();
          }

          window.location.href = props.href;
        }}
        {...(props.buttonProps || {})}
      >
        {props.label}
      </Button>
    );
  }

  return (
    <a
      href={props.href}
      onClick={
        props.onClick
          ? (e) => {
              e.preventDefault();
              props.onClick();
            }
          : undefined
      }
    >
      {props.label}
    </a>
  );
};

const buildDismissKey = (key: string): string => {
  return 'alerts.' + key;
};

export const Alert: FC<AlertProps> = ({
  action,
  title,
  message,
  severity = AlertSeverity.Info,
  dismissKey,
  icon,
  testId,
  className = '',
  onDismiss,
}) => {
  const isDismissed = !!dismissKey && !!localStorage.getItem(buildDismissKey(dismissKey));
  const [displayed, setDisplayed] = useState(!isDismissed);
  const displayDismissButton = !!dismissKey || !!onDismiss;

  if (!displayed) {
    return null;
  }

  const handleDismiss = (): void => {
    localStorage.setItem('alerts.' + dismissKey, 'true');
    setDisplayed(false);
  };

  return (
    <div
      className={classNames(
        `alert-${severity} relative`,
        'box-border flex flex-1 rounded border-0 border-l-4 border-solid p-6 shadow-light',
        'border-l-[color:--alert-color] bg-[color:--alert-bg]',
        className,
      )}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {displayDismissButton && (
        <BsX
          role="button"
          onClick={onDismiss ? onDismiss : handleDismiss}
          className="absolute right-4 top-4 cursor-pointer rounded my-auto hover:bg-black/10"
          {...(testId ? { 'data-testid': testId + '--dismiss' } : {})}
        />
      )}
      <div className="flex flex-1 flex-row">
        {!!icon && (
          <div
            className={classNames(
              'mr-12 ml-6 flex items-center justify-center border-0 border-r-2 border-solid pr-12 text-32',
              'border-r-[color:--alert-color] text-[color:--alert-color]',
            )}
          >
            {icon}
          </div>
        )}
        <div className="flex flex-col w-full">
          {!!title && <div className="w-full title font-bold p-0 m-0 text-[color:--alert-color]">{title}</div>}
          {!!message && <p className="!m-0 font-normal">{message}</p>}
        </div>
        {action && (
          <div>
            <AlertAction {...action} />
          </div>
        )}
      </div>
    </div>
  );
};
